import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { FormApi } from 'final-form';

import { FormControlLabel, Checkbox } from '@mui/material';
import cn from 'classnames';

import { CheckinPassengerFragment, PassengerFragment, TravellerParameterFragment } from '@websky/graphql';

import AdditionalServiceWrapper from '../../../../AdditionalServiceWrapper';

import Money from '../../../../Money/components/Money';
import PhoneDropdown from '../../../../BaseComponents/PhoneDropdown';

import { Money as MoneyType } from '../../../../types';

import { useTheme } from '../../../../theme';
import { useSmsPopupFormData, useSmsPopupValidation } from './hooks';
import { getTravellerName, useIsMobile } from '../../../../utils';
import { SafeHtml } from '@shared/ui';

import { TravellerFields } from '../../../../TravellerForm/types';

import { getFormTravellerKey, getTotalPrice, SmsPopupFormValues } from '../utils';

export interface Props {
	className?: string;
	smsNotificationId: string;
	title: string;
	description?: string;
	travellers: (PassengerFragment | CheckinPassengerFragment)[];
	customerValues: TravellerParameterFragment[];
	price?: MoneyType;
	onClose?: () => void;
	onSubmit: (values: SmsPopupFormValues, form?: FormApi<SmsPopupFormValues>) => void;
}

const SmsPopup: FC<Props> = ({
	className,
	smsNotificationId,
	title,
	description,
	travellers,
	customerValues,
	price,
	onClose,
	onSubmit
}) => {
	const { SmsPopup: css } = useTheme('SmsNotifications');
	const { t } = useTranslation('Checkout');

	const isMobile = useIsMobile();

	const { validate } = useSmsPopupValidation(
		customerValues.find(({ name: valueName }) => valueName === TravellerFields.Phone)?.validationRules[0].regExp
	);

	const { initialFormData, clearFormData, travellersPhoneOptions, isClearable } = useSmsPopupFormData(
		travellers,
		customerValues,
		smsNotificationId
	);

	return (
		<Form<SmsPopupFormValues> initialValues={initialFormData} onSubmit={onSubmit} validate={validate}>
			{({ handleSubmit, form, values, dirty }) => {
				const showClear = isClearable(values);

				return (
					<form className={css.form}>
						<AdditionalServiceWrapper
							className={cn(className, css.wrapper)}
							headerClassName={css.header}
							contentClassName={css.content}
							header={title}
							totalPrice={getTotalPrice(values, price)}
							onCancel={isMobile ? onClose : undefined}
							onConfirm={dirty ? handleSubmit : onClose}
							onClear={() => clearFormData(form)}
							showClear={showClear}
						>
							<>
								{description && <SafeHtml tag="div" className={css.description} html={description} />}
								{travellers.map(traveller => {
									const key = getFormTravellerKey(traveller.id);

									return (
										<div key={key} className={css.passenger}>
											<Field name={`${key}.selected`} type="checkbox">
												{field => (
													<FormControlLabel
														label={getTravellerName(traveller)}
														classes={{
															label: css.passenger__checkboxLabel
														}}
														control={
															<Checkbox
																{...field}
																checked={field.input.value}
																classes={{
																	root: css.checkbox,
																	checked: css.checkbox_checked
																}}
																disableRipple={true}
																onChange={e => field.input.onChange(e)}
																disabled={values[key].readOnly}
															/>
														}
													/>
												)}
											</Field>

											<div className={css.passenger__controls}>
												<Field name={`${key}.phone`}>
													{field => {
														const isDisabled = !values[key].selected;
														const error =
															!isDisabled && field.meta.touched && field.meta.error;

														const { onChange, multiple, ...input } = field.input;

														return (
															<PhoneDropdown
																className={css.passenger__dropdown}
																options={travellersPhoneOptions}
																InputProps={{
																	label: t('Phone number'),
																	error,
																	helperText: error ? field.meta.error : undefined
																}}
																{...input}
																disabled={isDisabled || values[key].readOnly}
																onInputChange={onChange}
																onChange={onChange}
															/>
														);
													}}
												</Field>

												{price && (
													<div className={css.passenger__price}>
														<Money money={price} />
													</div>
												)}
											</div>
										</div>
									);
								})}
							</>
						</AdditionalServiceWrapper>
					</form>
				);
			}}
		</Form>
	);
};

export default SmsPopup;
